import React, { useEffect, useMemo, useState } from 'react';
import { useGlobalContext } from 'GlobalContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { useModal } from 'components/modals/ModalContext';
import FormFieldContext from 'components/forms/FormFieldContext';
import { useFetchData } from 'services/api/useFetchData';
import axios, { CancelTokenSource } from 'axios';
import { v4 as uuidv4 } from 'uuid';
import '../../style/scss/timeline.scss';
import { saveData } from 'services/api/saveData';
import { TimelineItemType } from 'types/TimelineTypes';
import { createNewEmailMessage, EmailDomain, EmailMessage, EmailTemplate } from './EmailTypes';
import EmailFormFields from './EmailFormFields';
import { Division } from '../../views/environments/EnvironmentTypes';
import { normalizeString } from 'services/utils/normalizeString';
import { useSettings } from 'services/settings/SettingsContext';
import WarningModal from 'components/modals/WarningModal';
import { findUnresolvedTags, resolveTemplateTags } from '../settings/email/templates/resolveTemplateTags';
import { getEmailTemplateRecipients, recipientsToEmailString } from './getEmailTemplateRecipients';

interface EmailFormProps {
    viewKey?: string;
    placement: 'inline' | 'modal';
    timeline_page?: string;  // The object name of the timeline page, for example: 'organization'
    timeline_page_id?: number;  // The id of the timeline page, for example organization '1'
    emailId?: number;
    divisionId: number | null;
    connectedFeature?: string;
    defaultTemplateType?: string;
    objectData?: any
    closeForm?: () => void;
    onSubmitSuccess?: (newItem: TimelineItemType) => void;
}

const EmailForm: React.FC<EmailFormProps> = ({ 
    viewKey, placement, timeline_page, timeline_page_id, emailId, divisionId, 
    connectedFeature, defaultTemplateType, objectData, closeForm, onSubmitSuccess
}) => {
    const { environmentHash } = useAuthContext();
    const { setFloatingAlert, setUnsavedChanges, unsavedChangesMap, setErrorMessages } = useGlobalContext();
    const { modalStack, initializeModal, revealModal, closeModal } = useModal();
    const { environmentSettings, userPreferences } = useSettings();
    const [data, setData] = useState<EmailMessage | null>(null);
    const [emailTemplates, setEmailTemplates] = useState<EmailTemplate[]>([]);
    const [emailDomains, setEmailDomains] = useState<EmailDomain[]>([]);
    const [divisions, setDivisions] = useState<Division[]>([]);
    const [filteredCustomDomains, setFilteredCustomDomains] = useState<EmailDomain[]>([]);
    const [cancelSource, setCancelSource] = useState<CancelTokenSource | null>(null);
    const [updatedData, setUpdatedData] = useState<Record<string, any>>({});
    const [buttonLoader, setButtonLoader] = useState(false);
    const topModal = modalStack[modalStack.length - 1];
    const [isReadyToShow, setIsReadyToShow] = useState(false);
    const showInModal = placement === 'modal';
    
    // Fetch the data of the current email
    const { response: fetchedEmail, loading: emailLoading } = useFetchData({ 
        apiUrl: emailId ? `get_email/${emailId}` : '', 
        useGuard: true,
        guardValue: emailId
    });

    // Fetch the email templates
    const { response: fetchedEmailTemplates, loading: emailTemplatesLoading } = useFetchData({ 
        apiUrl: 'get_email_template_list', 
        params: useMemo(() => ({ deleted: false }), []) 
    });

    // Fetch the email domains
    const { response: fetchedEmailDomains, loading: emailDomainsLoading } = useFetchData({ 
        apiUrl: 'get_email_domain_list', 
        params: useMemo(() => ({ deleted: false }), []) 
    });

    // Fetch the divisions
    const { response: fetchedDivisions, loading: divisionsLoading } = useFetchData({ 
        apiUrl: 'get_division_list',
        params: useMemo(() => ({ is_active: true }), []) 
    });

    // Fetch the environment settings
    const { response: fetchedEnvironmentSettings, loading: environmentSettingsLoading } = useFetchData({ 
        apiUrl: `get_environment_settings/${environmentHash}`
    });

    // Filter the email templates on the connected feature and division
    useEffect(() => {
        if (fetchedEmailTemplates) {
            // Initialize variable to store the filtered templates
            let templates = fetchedEmailTemplates.results;

            // Only show manual templates in the email form
            templates = templates.filter((template: EmailTemplate) => template.send_method === 'manual');

            // When the email form is opened from an object, show the templates of that object
            // and show the templates which are not added to a feature.
            // Some features also include templates from related features (e.g. jobs includes appointments).
            if (connectedFeature) {
                const relatedFeaturesMap: Record<string, string[]> = {
                    jobs: ['jobs', 'appointments'],
                };
                const features = relatedFeaturesMap[connectedFeature] ?? [connectedFeature];
                templates = templates.filter((template: EmailTemplate) =>
                    !template.connected_feature || features.includes(template.connected_feature)
                );
            }

            // Only show the templates belonging to the same division. When no division is given, only
            // show the templates which are not linked to a division
            templates = templates.filter((template: EmailTemplate) => {
                if (divisionId !== null) {
                    return template.division === divisionId;
                }
                return template.division === null;
            });

            setEmailTemplates(templates);
        }
    }, [fetchedEmailTemplates, divisionId, connectedFeature]);

    // Set the fetched email domains
    useEffect(() => {
        if (fetchedEmailDomains) {
            setEmailDomains(fetchedEmailDomains.results);
        };
    }, [fetchedEmailDomains]);

    // Set the fetched divisions
    useEffect(() => {
        if (fetchedDivisions) {
            setDivisions(fetchedDivisions.results);
        };
    }, [fetchedDivisions]);

    // Set the email data
    useEffect(() => {
        if (data !== null) return;

        // Set the email data for an existing email
        if (emailId && fetchedEmail) {
            setData(fetchedEmail);
            return;
        }

        // Create a new email
        if (!emailId) {
            const newEmail = createNewEmailMessage(uuidv4());
            setData(newEmail);
        }
    }, [emailId, data, fetchedEmail, fetchedEmailTemplates]);

    // Set the default template, if given, as initial data
    useEffect(() => {
        if (!data || !fetchedEmailTemplates) return;

        if (!data.template && defaultTemplateType) {
            // Filter the fetched templates on the given default template type
            let templates = fetchedEmailTemplates.results.filter((template: EmailTemplate) =>
                !template.deleted &&
                template.template_type === defaultTemplateType &&
                template.send_method === 'manual'
            );

            // Only match templates belonging to the same division.
            // When no division is given, only match templates without a division.
            const matchingTemplates = templates.filter((template: EmailTemplate) => {
                if (divisionId !== null) {
                    return template.division === divisionId;
                }

                return template.division === null;
            });

            // Select the data from the template when there is one matching template
            if (matchingTemplates.length === 1) {
                const template = matchingTemplates[0];

                // Use the templates own connected feature for tag resolution
                const effectiveFeature = template.connected_feature || connectedFeature;

                // Resolve the tags in the subject with the object data
                const resolvedSubject = resolveTemplateTags({
                    text: template.subject ?? '',
                    connectedFeature: effectiveFeature,
                    objectData,
                    currentUser: {
                        first_name: userPreferences.first_name,
                        last_name: userPreferences.last_name
                    },
                    locale: environmentSettings?.default_locale,
                    language: environmentSettings?.default_language,
                    currency: environmentSettings?.default_currency ?? 'EUR',
                    multilineSeparator: ', '
                });

                // Resolve the tags in the body with the object data
                const resolvedBody = resolveTemplateTags({
                    text: template.body ?? '',
                    connectedFeature: effectiveFeature,
                    objectData,
                    currentUser: {
                        first_name: userPreferences.first_name,
                        last_name: userPreferences.last_name
                    },
                    locale: environmentSettings?.default_locale,
                    language: environmentSettings?.default_language,
                    currency: environmentSettings?.default_currency ?? 'EUR',
                    multilineSeparator: '<br>'
                });

                // Get the recipients from the email template and object data
                const recipients = getEmailTemplateRecipients({ template, objectData });

                // Build the template values for the email data
                const templateValues = {
                    template: template.id ?? null,
                    to: recipientsToEmailString(recipients),
                    bcc: template.default_bcc_recipient ?? null,
                    custom_domain: template.custom_domain ?? null,
                    from_email: template.from_email ?? '',
                    subject: resolvedSubject,
                    body: resolvedBody,
                    attachments: template.default_attachments ?? [],
                    add_object_pdf: template.add_object_pdf ?? false
                };

                setData(prev => prev ? { ...prev, ...templateValues } : prev);

                setUpdatedData(prev => ({ ...prev, ...templateValues }));
            }
        }

        setIsReadyToShow(true);
    }, [data, fetchedEmailTemplates, divisionId, divisions, defaultTemplateType]);

    // Set the default sender details
    useEffect(() => {
        if (!data || !fetchedEmailDomains || data.from_email || !fetchedEnvironmentSettings) return;

        // Get the company name of the main division from the environment settings
        const companyName = fetchedEnvironmentSettings.company_name;

        // Initialize the from values
        let from_name = companyName;
        let normalizedName = normalizeString(companyName);
        let from_email = `${normalizedName}@deliver.getcrafter.com`;
        let custom_domain: number | null = null;
        let filteredDomains: EmailDomain[] = [];

        // If a division id is given from the parent component, use the name of the division
        if (divisionId) {
            const currentDivision = divisions.find(division => division.id === divisionId);
            if (currentDivision) {
                from_name = currentDivision.name;
                normalizedName = normalizeString(currentDivision.name);
                from_email = `${normalizedName}@deliver.getcrafter.com`;
            }
        }

        // Set the details if the environment uses a custom domain
        if (fetchedEnvironmentSettings.email_domain_method === 'custom') {
            // Get the non-deleted email domains, division custom domains and custom domain of the main division
            const customEmailDomains = emailDomains.filter(domain => !domain.deleted);
            const divisionDomains = customEmailDomains.filter(domain => domain.division === divisionId);
            const mainDomains = customEmailDomains.filter(domain => domain.is_main);

            // If there is a custom domain for the division, set it as selected
            if (divisionDomains.length > 0) {
                const selected = divisionDomains[0];
                from_email = `${normalizedName}@${selected.domain}`;
                custom_domain = selected.id;
                filteredDomains = divisionDomains;

            // If there are no division domains set, use the custom main domain
            } else if (mainDomains.length > 0) {
                const selected = mainDomains[0];
                from_email = `${normalizedName}@${selected.domain}`;
                custom_domain = selected.id;
                filteredDomains = mainDomains;
            }
        }

        // Prevent set state update
        if (data.from_email === from_email) return;

        // Update the states
        setData(prev => prev ? { ...prev, custom_domain, from_email, from_name } : prev);
        setUpdatedData(prev => ({ ...prev, custom_domain, from_email, from_name }));  
        setFilteredCustomDomains(filteredDomains);
    }, [data?.from_email, divisionId, divisions, emailDomains, fetchedEnvironmentSettings]);

    // Reveal the modal if the data is ready
    useEffect(() => {
        if (showInModal && data && isReadyToShow) {
            revealModal();
        };
    }, [showInModal, data, isReadyToShow]);

    // Set unsaved changes when updated data changes
    useEffect(() => {
        if (!data || !updatedData) return;

        // Check if the updated data contains different values than the original data
        const hasChanges = Object.entries(updatedData).some(([key, value]) => {
            return data[key as keyof EmailMessage] !== value;
        });

        // Set unsaved changes
        if (viewKey && (unsavedChangesMap[viewKey] !== hasChanges) && showInModal) {
            setUnsavedChanges(viewKey, hasChanges);
        }
    }, [data, updatedData]);
    
    // Handle delete
    const handleDelete = async () => {
        if (data && data.id) {
            try {
                // Call the delete email function on the backend
                const response = await saveData({ 
                    apiUrl: `delete_email/${data.id}`,
                    method: 'patch',
                    data: {},
                    source: axios.CancelToken.source()
                })

                // Handle successful response
                if (response && response.status === 200) {
                    // Show success alert
                    setFloatingAlert({ 'type': 'success' });

                    // Update the timeline items state
                    onSubmitSuccess?.({
                        ...response.data,
                        timeline_object_type: 'email'
                    });

                    closeForm?.();
        
                    if (showInModal) {
                        closeModal();
                    }
                }
            } catch (error) {
                setFloatingAlert({ type: 'danger', message: 'timeline.email.alert.delete_email_failed_message' })
            }
        }
    }

    // Handle submit
    const handleSubmit = async () => {

        // Get the final subject and body values to check for unresolved tags
        const finalSubject = 'subject' in updatedData ? updatedData.subject : data?.subject;
        const finalBody = 'body' in updatedData ? updatedData.body : data?.body;

        // Find the tags which are not converted to a value
        const unresolvedTags = Array.from(new Set([
            ...findUnresolvedTags(finalSubject ?? ''),
            ...findUnresolvedTags(finalBody ?? '')
        ]));

        // Show a warning modal when the email still contains unresolved tags
        if (unresolvedTags.length > 0) {
            initializeModal(
                <WarningModal
                    header='email.general.unresolved_tags_warning_header'
                    message='email.general.unresolved_tags_warning_message'
                    messageValues={{ tags: unresolvedTags.map(tag => `{${tag}}`).join(', ') }}
                    buttonLabel='email.general.unresolved_tags_send_anyway_button'
                    onApproval={() => { submitEmail(); }}
                />,
                { modalSize: 'extra-small' }
            );
            return;
        }

        await submitEmail();
    };

    // Submit the email to the backend
    const submitEmail = async () => {
        setButtonLoader(true);

        // Create the cancel token source
        const source = axios.CancelToken.source();
        setCancelSource(source);

        // Add the given timeline page and timeline page id to the submit data
        const submitData = {
            ...updatedData,
            division: divisionId,
            [timeline_page as string]: timeline_page_id
        }

        try {
            // Save the data in the backend
            const response = await saveData({ 
                apiUrl: 'post_email_message', 
                method: 'post', 
                data: submitData, 
                source 
            });

            // Handle successful response
            if (response && (response.status === 200 || response.status === 201)) {
                // Show success alert
                setFloatingAlert({ 'type': 'success' });

                setButtonLoader(false);

                // Update the timeline items state
                onSubmitSuccess?.({
                    ...response.data,
                    timeline_object_type: 'email'
                });

                if (viewKey) {
                    setUnsavedChanges(viewKey, false);
                }

                closeForm?.();

                if (showInModal) {
                    // Call the on success callback to refresh the list
                    if (topModal?.props?.onSuccess) {
                        topModal.props.onSuccess(response.data);
                    };

                    closeModal();
                }
            }
        } catch (error: any) {
            if (error.response && error.response.status === 400) {
                setErrorMessages(error.response.data);
            } else {
                setFloatingAlert({ type: 'danger', message: 'validation.general.save_failed' })
            }

            setButtonLoader(false);
        }
    };

    // Handles the closing of the note form
    const handleClose = () => {
        if (cancelSource) {
            cancelSource.cancel();
        }

        closeForm?.();
        setErrorMessages({});
        setButtonLoader(false);

        if (showInModal) {
            closeModal();
        }
    };

    // Determine to show the loader
    const shouldShowLoader = 
        emailTemplatesLoading !== 'success' ||
        emailDomainsLoading !== 'success' ||
        divisionsLoading !== 'success' ||
        environmentSettingsLoading !== 'success' ||
        (emailId && emailLoading !== 'success');

    return (
        (shouldShowLoader || !data) && placement === 'inline' ? (
            <div className='inline-form-loader'>
                <div className="loader"></div>
            </div>
        ) : (
            data && (
                <FormFieldContext.Provider value={{ updatedData, setUpdatedData }}>
                    <EmailFormFields
                        placement={placement}
                        initialData={data}
                        emailTemplates={emailTemplates}
                        customDomains={filteredCustomDomains}
                        connectedFeature={connectedFeature}
                        objectData={objectData}
                        buttonLoader={buttonLoader}
                        handleClose={handleClose}
                        handleDelete={handleDelete}
                        handleSubmit={handleSubmit}
                    />
                </FormFieldContext.Provider>
            )
        )
    );
};

export default EmailForm;